<script setup lang="ts">
import { computed } from 'vue';
import type { ComponentGroup, StatusSnapshot } from '@/types';
import { statusMessages } from '@/i18n';
import {
  componentUptime,
  formatUptime,
  hasLatencyData,
  rollupGroupStatus,
  statusLabel,
  statusToneClass,
} from '@/utils/status';
import UptimeBar from './UptimeBar.vue';
import ResponseTimeChart from './ResponseTimeChart.vue';

const props = defineProps<{
  group: ComponentGroup;
  snapshot: StatusSnapshot;
  uptimeWindowDays: number;
}>();

const groupStatus = computed(() => rollupGroupStatus(props.snapshot, props.group.id));
const groupToneClass = computed(() => statusToneClass(groupStatus.value));
const groupStatusLabel = computed(() => statusLabel(groupStatus.value));
</script>

<template>
  <section class="group-card">
    <header class="group-header" :class="groupToneClass">
      <h2>{{ group.name }}</h2>
      <span class="group-status">{{ groupStatusLabel }}</span>
    </header>
    <ul class="component-list">
      <li
        v-for="component in group.components"
        :key="component.id"
        class="component-row"
        :class="statusToneClass(component.status)"
      >
        <div class="component-meta">
          <span class="component-dot" aria-hidden="true"></span>
          <span class="component-name">{{ component.name }}</span>
        </div>
        <UptimeBar
          :component-id="component.id"
          :history="snapshot.history"
          :window-days="uptimeWindowDays"
          :uptime="formatUptime(componentUptime(component))"
        />
        <ResponseTimeChart
          v-if="hasLatencyData(component.latency)"
          :series="component.latency"
        />
        <p v-else class="component-no-latency">{{ statusMessages.responseTimesNoData }}</p>
      </li>
    </ul>
  </section>
</template>

<style scoped>
.group-card {
  border: 1px solid var(--xt-border);
  border-radius: 0.5rem;
  background: var(--xt-bg-elevated);
  overflow: hidden;
}

.group-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid var(--xt-border);
  background: var(--xt-tone-bg-soft);
}

.group-header h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--xt-fg-strong);
}

.group-status {
  font-size: 0.8rem;
  font-weight: 500;
  color: var(--xt-tone-fg);
}

.component-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.component-row {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.75rem 1.25rem 1rem;
  border-top: 1px solid var(--xt-border-subtle);
}

.component-no-latency {
  margin: 0;
  font-size: 0.7rem;
  color: var(--xt-fg-muted);
}

.component-row:first-child {
  border-top: none;
}

.component-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.component-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--xt-tone-fg);
}

.component-name {
  font-size: 0.9rem;
  color: var(--xt-fg-strong);
}
</style>
